import { useState } from "react";
import { Link } from "react-router-dom";
import { useT } from "../../i18n";
import { formatMileage, formatPrice } from "../../lib/format";
import { StatusBadge } from "../VehicleCard";

export const SummaryPanel = ({ v, onEnquire, whatsapp, line, shareUrl, similarTo, availableTo }) => {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const share = async () => {
    if (navigator.share) return navigator.share({ url: shareUrl, title: `${v.model_year} ${v.make} ${v.model}` }).catch(() => {});
    await navigator.clipboard?.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const sold = v.status === "sold";
  const p = v.price;
  const japan = p?.kind === "japan" && p.amount != null;
  const specs = [
    ["labels.mileage", formatMileage(v.mileage_km)],
    ["labels.fuel", v.fuel_type && t(`enums.fuel_type.${v.fuel_type}`)],
    ["labels.transmission", v.transmission && t(`enums.transmission.${v.transmission}`)],
    ["labels.drivetrain", v.drivetrain && t(`enums.drivetrain.${v.drivetrain}`)],
  ].filter(([, val]) => val);

  return (
    <div data-testid="vehicle-summary">
      <p className="micro-label tabular-nums text-white/55" data-testid="summary-make-stock">{v.make} · {v.stock_id}</p>
      <h1 className="font-display mt-3 text-4xl leading-[1.08] lg:text-[2.75rem]" data-testid="summary-title">
        {v.model} {v.grade && <span className="text-white/55">{v.grade}</span>}
      </h1>
      <p className="mt-2 tabular-nums text-white/60" data-testid="summary-year">{v.model_year}</p>
      {(v.status === "reserved" || sold) && (
        <div className="mt-6 flex items-start gap-4" data-testid="summary-status">
          <StatusBadge status={v.status} inline />
          <p className="text-sm text-white/60">{t(sold ? "detail.soldNote" : "detail.reservedNote")}</p>
        </div>
      )}
      <div className="mt-8 border-t border-white/15 pt-5" data-testid="summary-price-block" data-kind={v.price?.kind}>
        <p className="micro-label text-white/45" data-testid="summary-price-label">{japan ? t("price.vehiclePrice") : t("price.price")}</p>
        <p className={`font-display mt-2 tabular-nums lining-nums ${v.price?.amount == null ? "text-2xl" : "text-4xl"} ${sold ? "text-white/45" : ""}`} data-testid="summary-price">
          {formatPrice(v, t)}{japan && <span className="ml-2 font-sans text-sm text-white/60">{t("price.exclTax")}</span>}
        </p>
        {japan && <p className="mt-1 text-sm tabular-nums text-white/60" data-testid="summary-price-incl">¥{p.vehicle_price_jpy.toLocaleString("en-US")} {t("price.inclTax")}</p>}
        {japan && (
          <dl className="mt-4 grid gap-1.5 text-sm tabular-nums text-white/65" data-testid="summary-price-breakdown">
            {[["price.miscFees", p.misc_fees_jpy, "fees"], ["price.totalLabel", p.total_payment_jpy, "total"]].map(([label, val, id]) => (
              <div key={id} className="flex justify-between gap-6 border-b border-white/10 pb-1.5">
                <dt>{t(label)}</dt>
                <dd data-testid={`summary-price-${id}`}>{val == null ? "—" : `¥${val.toLocaleString("en-US")}`}</dd>
              </div>
            ))}
          </dl>
        )}
        {japan && p.conditions && <p className="mt-3 text-xs leading-relaxed text-white/50" data-testid="summary-price-conditions">{p.conditions}</p>}
        {p?.shipping_note && !p.hidden && <p className="mt-3 text-xs text-white/55" data-testid="summary-shipping-note">{t("price.shipping")}</p>}
        {p?.kind === "export" && !p.hidden && <p className="mt-2 text-xs leading-relaxed text-white/45" data-testid="summary-export-tax-note">{t("price.exportTaxNote")}</p>}
      </div>
      {specs.length > 0 && (
        <dl className="mt-6 grid grid-cols-2 border-l border-t border-white/10" data-testid="summary-key-specs">
          {specs.map(([label, value]) => (
            <div key={label} className="border-b border-r border-white/10 px-4 py-3.5">
              <dt className="micro-label text-white/40">{t(label)}</dt>
              <dd className="mt-1.5 text-[15px] tabular-nums">{value}</dd>
            </div>
          ))}
        </dl>
      )}
      <p className="mt-5 flex items-center gap-3 text-sm text-white/65" data-testid="summary-availability">
        <span className="h-px w-5 bg-white/40" aria-hidden />
        {t(`detail.availability.${v.market}`)}
      </p>
      <div className={`mt-8 flex-col gap-2.5 ${sold ? "flex" : "hidden lg:flex"}`}>
        {sold ? (
          <>
            <Link to={similarTo} className="btn btn-light" data-testid="detail-find-similar">{t("detail.findSimilar")}</Link>
            <Link to={availableTo} className="btn btn-ghost-light" data-testid="detail-view-available">{t("detail.viewAvailable")}</Link>
          </>
        ) : (
          <>
            <button type="button" onClick={onEnquire} className="btn btn-light" data-testid="detail-enquire">{t("detail.enquire")}</button>
            {whatsapp && (
              <a href={whatsapp} target="_blank" rel="noopener noreferrer" className="btn btn-ghost-light" data-testid="detail-whatsapp">{t("detail.whatsapp")}</a>
            )}
            {line && (
              <a href={line} target="_blank" rel="noopener noreferrer" className="btn btn-ghost-light" data-testid="detail-line">{t("detail.line")}</a>
            )}
          </>
        )}
        <button type="button" onClick={share} className="micro-label mt-2 self-start text-white/60 underline-offset-4 hover:text-white hover:underline" data-testid="detail-share">
          {copied ? t("detail.linkCopied") : t("detail.share")}
        </button>
      </div>
    </div>
  );
};
